/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useState, useRef} from 'react';
import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Radio,
	Select,
	Space,
	TimePicker,
} from "antd";
import dayjs from "dayjs";
import styles from './MakeBaecha.module.scss';
import { emptySearchGroup, GroupSearchSelect } from "../../../components/common/selector/GroupSearchSelect";
import groupAPI from "../../../api/groupAPI";
import {generalOptions, guljeolOptions, Option} from "./MakeBaecha.interface";
import BaljuDTO from "./dto/BaljuDTO";
import {BaechaType} from "../../../types/Type";
import {createBaljuAPI} from "../../../api/baechaAPI";
import memberAPI from "../../../api/memberAPI";


interface MakeBaechaProps {
	onFinish: () => any;
}

const MakeBaecha = ({ onFinish }: MakeBaechaProps) => {
	const [form] = Form.useForm();
	const [selectedGroup, setSelectedGroup] = useState(emptySearchGroup);
	const [memberOptions, setMemberOptions] = useState<Option[]>([]);
	const [loading, setLoading] = useState(false);
	const [isGuljeol, setIsGuljeol] = useState(false);
	const [carTypeOptions, setCarTypeOptions] = useState<Option[]>(generalOptions);
	const [groupRule, setGroupRule] = useState<any>({});
	const [memberCarList, setMemberCarList] = useState<any[]>([]);

	const latestGroupIdRef = useRef<string>("");

	const onClickGroupName = async (data: any) => {
		setSelectedGroup(data);

		setMemberOptions([]);
		form.resetFields(['member']);

		latestGroupIdRef.current = data.value;

		setLoading(true);
		try {
			const list = await groupAPI.fetchGroupMemberList(data.value);
			const rules = await groupAPI.getGroupConfigs(data.value);
			setGroupRule(rules.config);
			if (latestGroupIdRef.current !== data.value) return;

			const options: Option[] = list.map((item: any) => ({
				value: item.id,
				label: item.nickname,
			}));
			setMemberOptions(options);
		} finally {
			if (latestGroupIdRef.current === data.value) setLoading(false);
		}
	};

	const onSelectBaljuMember = async (memberId: string) => {
		form.resetFields(['baljuMemberCarId']);
		const list = await memberAPI.getMemberCarList(memberId);
		const newMemberCarList = list.filter((item: any) => item.memberId === memberId)
			.map((item: any) => ({
				value: item.memberCarId,
				label: `${item.carType} ${item.number}`,
			}));
		setMemberCarList(newMemberCarList);
		form.setFieldValue('baljuMemberCarId', newMemberCarList[0]?.value);
	};

	const getBaechaType = (isUrgent: boolean | undefined, designatedMemberId: string | undefined): BaechaType => {
		if (isUrgent) return 'urgent';
		if (designatedMemberId) return 'designated';
		return 'normal';
	};

	const getWorkHour = (values: any) => {
		const durationSelect = values.durationSelect;

		if (durationSelect === '1h') return 1;
		if (durationSelect === '2h') return 2;
		if (durationSelect === '4h') return 4;
		return undefined;
	};

	const getWorkDay = (values: any) => {
		const durationSelect = values.durationSelect;
		const durationDays = values.durationDays;

		if (durationSelect === '1d') return 1;
		if (durationSelect === '2d') return 2;
		if (durationSelect === '3d') return 3;
		if (durationSelect === 'm') return 0;
		return durationDays;
	};

	const getWorkArriveDate = (values: any) => {
		const date = values.date;
		const time = values.time;

		return dayjs(date)
			.tz('Asia/Seoul')
			.set('hour', time.hour())
			.set('minute', time.minute())
			.toDate();
	};

	const onSubmit = async (values: any) => {
		const baljuDTO = new BaljuDTO({
			baechaType: getBaechaType(values.isUrgent, values.designatedMemberId),
			baljuGroupId: values.group.value,
			baljuMemberCarId: values.baljuMemberCarId,
			baljuMemberId: values.baljuMemberId,
			baljuingGroupId: values.group.value,
			carDetail: values.carDetail?.join(', '),
			carType: values.carType,
			designatedMemberId: values.designatedMemberId,
			isLink: values.isLink,
			isNoFee: values.isNoFee,
			isSpecificCarOnly: values.isSpecificCarOnly,
			isWorkMonth: values.durationSelect === 'm',
			price: values.price * 10_000,
			priceReceiveMethod: values.priceReceiveMethod,
			workArriveDate: getWorkArriveDate(values),
			workContent: values.workContent,
			workEtc: values.workEtc?.join(', '),
			workDay: getWorkDay(values),
			workHour: getWorkHour(values),
			workLoc: values.workLoc,
			privatePhone: values.privatePhone,
			privateAddress: values.privateAddress,
			privateNote: values.privateNote,
			latitude: values.latitude,
			longitude: values.longitude,
		});

		try {
			await createBaljuAPI(baljuDTO);
			onFinish();
		} catch (e) {
			console.log('발주 에러');
			console.error(e);
		}
	};

	return (
		<div className={styles.wrap}>
			<Form
				form={form}
				onFinish={onSubmit}
				style={{ paddingTop: '20px' }}
			>
				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					{/* 배차방 */}
					<Form.Item name="group" label="배차방" rules={[{ required: true, message: '배차방을 선택하세요' }]} style={{ width: '200px'}}>
						<GroupSearchSelect
							selectedGroup={selectedGroup}
							onChange={onClickGroupName}
							placeholder="배차방 검색"
						/>
					</Form.Item>

					{/* 발주 멤버 */}
					<Form.Item name="baljuMemberId" label="발주 멤버" rules={[{ required: true, message: '발주 멤버를 선택하세요' }]} style={{ width: '250px'}}>
						<Select
							placeholder="발주 멤버 선택"
							options={memberOptions}
							loading={loading}
							allowClear
							showSearch
							optionFilterProp="label"
							onChange={onSelectBaljuMember}
						/>
					</Form.Item>
				</Space>


				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item label="차량 유형">
						<Radio.Group
							value={isGuljeol ? 'guljeol' : 'general'}
							onChange={(e) => {
								const selected = e.target.value;
								const isGul = selected === 'guljeol';
								setIsGuljeol(isGul);

								setCarTypeOptions(isGul ? guljeolOptions : generalOptions);

								form.setFieldValue('carType', undefined);
								form.setFieldValue('carDetail', []);
								form.setFieldValue('isSpecificCarOnly', false);
							}}
						>
							<Radio value="general">일반</Radio>
							<Radio value="guljeol">굴절</Radio>
						</Radio.Group>
					</Form.Item>

					{/* 차량	*/}
					<Form.Item name="carType" label="차량" rules={[{ required: true, message: '차량을 선택하세요' }]}>
						<Select
							placeholder="차량 선택"
							style={{ width: 200 }}
							options={carTypeOptions}
						/>
					</Form.Item>
				</Space>

				{!isGuljeol && (
					<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
						<Form.Item name="isSpecificCarOnly" valuePropName="checked">
							<Checkbox
								onChange={(e) => {
									const checked = e.target.checked;
									setCarTypeOptions((prev) =>
										prev.map((item) => {
											if (!checked) {
												return { ...item, label: item.label.replace(/만$/, '') };
											}

											if (!item.label.endsWith('만')) {
												return { ...item, label: `${item.label}만` };
											}

											return item;
										})
									);
								}}
							>
								차종 제한
							</Checkbox>
						</Form.Item>

						<Form.Item name="carDetail" label="추가 옵션" style={{ width: '350px' }}>
							<Select
								mode="tags"
								style={{ width: "100%" }}
								placeholder="옵션 선택 또는 직접 입력"
								tokenSeparators={[","]}
								options={[
									{ label: "저상", value: "저상" },
									{ label: "단축", value: "단축" },
									{ label: "중축", value: "중축" },
									{ label: "붐대 M 이상", value: "붐대 M 이상" },
									{ label: "전고 M 이하", value: "전고 M 이하" },
								]}
							/>
						</Form.Item>
					</Space>
				)}

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item name="isUrgent" valuePropName="checked" style={{ width: '90px' }}>
						<Checkbox
							onChange={(e) => {
								if (e.target.checked) {
									form.setFieldsValue({ date: dayjs() });
								}
							}}
						>
							긴급 배차
						</Checkbox>
					</Form.Item>
					<Form.Item name="date" label="도착일" rules={[{ required: true, message: '도착일을 선택하세요' }]}>
						<DatePicker
							disabled={form.getFieldValue('isUrgent')}
							showToday={false}
							renderExtraFooter={() => (
								<Space style={{ width: "100%", display: 'flex', justifyContent: 'space-evenly' }}>
									<Button type="link" size="small" onClick={() => form.setFieldsValue({ date: dayjs() })}>
										오늘
									</Button>
									<Button type="link" size="small" onClick={() => form.setFieldsValue({ date: dayjs().add(1, "day") })}>
										내일
									</Button>
									<Button type="link" size="small" onClick={() => form.setFieldsValue({ date: dayjs().add(2, "day") })}>
										모레
									</Button>
								</Space>
							)}
						/>
					</Form.Item>
					<Form.Item name="time" label="도착시간" rules={[{ required: true, message: '도착시간을 선택하세요' }]}>
						<TimePicker format="HH:mm" />
					</Form.Item>
				</Space>

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item
						name="durationSelect"
						label="작업일정"
						rules={[{ validator: (_, value) => (value || form.getFieldValue('durationDays'))
								? Promise.resolve()
								: Promise.reject(new Error('작업일정을 선택하거나 직접 입력하세요')),
						}]}
					>
						<Select
							style={{ width: 160 }}
							placeholder="작업일정 선택"
							options={[
								{ label: '1시간', value: '1h' },
								{ label: '2시간', value: '2h' },
								{ label: '반나절', value: '4h' },
								{ label: '하루', value: '1d' },
								{ label: '2일', value: '2d' },
								{ label: '3일', value: '3d' },
								{ label: '월대', value: 'm' },
							]}
							onChange={() => {
								form.resetFields(['durationDays']);
							}}
						/>
					</Form.Item>

					<Form.Item
						name="durationDays"
						rules={[{ validator: (_, value) => (value || form.getFieldValue('durationSelect'))
							? Promise.resolve()
							: Promise.reject(new Error('작업일정을 선택하거나 직접 입력하세요')),
						}]}
					>
						<InputNumber
							min={1}
							placeholder="직접 입력"
							onChange={() => {
								form.resetFields(['durationSelect']);
							}}
							addonAfter="일"
						/>
					</Form.Item>
				</Space>

				<Form.Item name="price" label="총액" rules={[{ required: true, message: '총액을 입력하세요' }]}>
					<InputNumber addonAfter="만 원" style={{ width: '100%' }} />
				</Form.Item>

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item name="isNoFee" valuePropName="checked">
							<Checkbox>무콜</Checkbox>
					</Form.Item>
					<Form.Item
						name="priceReceiveMethod"
						label="결제"
						rules={[{ required: true, message: '결제 방법을 선택하세요' }]}
						initialValue="direct_ad"
					>
							<Radio.Group>
								<Radio value="direct_ad">직수</Radio>
								<Radio value="direct_noad">현수</Radio>
								<Radio value="sign">싸인</Radio>
							</Radio.Group>
					</Form.Item>
				</Space>

				<Form.Item name="workLoc" label="주소" rules={[{ required: true, message: '현장 주소를 입력하세요' }]}>
					<Input placeholder="문정동, 갈현동" />
				</Form.Item>

				<Form.Item name="workContent" label="작업 내용">
					<Input />
				</Form.Item>

				<Form.Item name="workEtc" label="전달 및 특이사항">
					<Select
						mode="tags"
						style={{ width: "100%" }}
						placeholder="옵션 선택 또는 직접 입력"
						tokenSeparators={[","]}
						options={[
							{ label: "친절하신분", value: "친절하신분" },
							{ label: "영업금지", value: "영업금지" },
							{ label: "시간예민X", value: "시간예민X" },
							{ label: "1군서류", value: "1군서류" },
							{ label: "개인차주", value: "개인차주" },
							{ label: "거래처", value: "거래처" },
							{ label: "경력자", value: "경력자" },
						]}
					/>
				</Form.Item>

				<Form.Item
					name="baljuMemberCarId"
					label="점수 적립할 차량"
					hidden={!groupRule.enable_point_to_car}
				>
					<Select
						placeholder="점수 적립할 차량 선택"
						options={memberCarList}
						loading={loading}
						allowClear
						showSearch
						optionFilterProp="label"
					/>
				</Form.Item>

				<Form.Item name="designatedMemberId" label="지정 배차">
					<Select
						placeholder="지정배차 멤버 선택"
						options={memberOptions}
						loading={loading}
						allowClear
						showSearch
						optionFilterProp="label"
					/>
				</Form.Item>

				<Form.Item name="isLink" valuePropName="checked">
					<Checkbox>연합배차 여부</Checkbox>
				</Form.Item>

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
					<Form.Item>
						<Button onClick={onFinish}>
							취소
						</Button>
						<Button type="primary" htmlType="submit"  style={{ marginLeft: '15px' }}>
							저장
						</Button>
					</Form.Item>
				</Space>
			</Form>
		</div>
	);
};

export default MakeBaecha;
